import React from 'react';
import { PlusIcon } from 'lucide-react';
import type { MirroredEvent } from '../../utils/calendarMirror';
import type { CalendarEntry } from '../../utils/calendarEntries';
import type { Entry } from './CalendarMonth';

/**
 * One square of the studio calendar.
 *
 * Three layers, loudest first, and the order is the whole point of the grid:
 *
 *   1. SHOOT DAYS — bright, solid left bar. This is a production calendar.
 *   2. PORTAL OS ENTRIES — the diary things Tom types here (a client meeting,
 *      a recce). Dimmer bar: ours, editable, but not production. Added
 *      13 Sep 2026.
 *   3. THE COMPANY CALENDAR — no bar at all, quietest, and a link out to
 *      Google rather than something you can edit here. Somebody else's entry.
 *
 * Entries Portal OS itself put in Google are filtered out of layer 3 before
 * they reach this component, so each real-world thing is drawn once. Same
 * rule that already stops Zeena's "ATM Filming" appearing beside Portal OS's
 * ATM shoot day.
 *
 * `isFocused` (24 Sep 2026) is none of those three. It is the day you pressed
 * on the Week Ahead to get here — a pointer, not a state — so it is drawn as
 * a hairline around the whole cell rather than as anything resembling a chip,
 * and the page above clears it the moment you do something.
 *
 * ---
 *
 * **TODAY IS INVERTED — 1 Nov 2026.** Tom: *"Is there a way to make TODAY a
 * bit more noticeable - I can never see which is today's date easily."*
 *
 * It had been marked two ways, and both were too quiet to find. The numeral
 * went from `paper/45` to `paper` — a brightness step on one character among
 * forty-two, on a page where the brightest thing is meant to be a shoot — and
 * beside it sat the word TODAY at `paper/40`, which is to say the label was
 * set DIMMER than the number it was qualifying. Nothing on the grid was
 * louder than its neighbours by enough to catch an eye crossing the page.
 *
 * The date now sits in a solid paper chip with ink type. This page has no
 * colour to spend, so the one move left is inversion — and it is used exactly
 * once, on one cell in the month, which is what makes it work. The cell
 * itself also takes a faint wash, so the column can be found from a distance
 * before the numeral is even legible.
 *
 * **It must not look like `isFocused`.** Those mean different things — one is
 * a fact about the date, the other is where you just came from — so they are
 * given different instruments: today is a filled chip, focus is a ring around
 * the cell. When a day is both, the ring wins the cell and the chip still
 * marks the date, which reads correctly as two true things at once.
 */
const MAX_EVENTS_PER_DAY = 3;

/** What a shoot chip says. The client first — it is what staff scan for. */
function chipLabel(entry: Entry): string {
  const what = entry.shoot.title || entry.project?.name || 'Shoot';
  return entry.project?.client ? `${entry.project.client} · ${what}` : what;
}
interface CalendarDayProps {
  iso: string;
  dayNumber: number;
  inMonth: boolean;
  isToday: boolean;
  /** Arrived here by pressing this day on the Week Ahead. */
  isFocused?: boolean;
  shoots: Entry[];
  entries: CalendarEntry[];
  events: MirroredEvent[];
  selectedShootId: string;
  selectedEntryId: string;
  onSelectShoot: (entry: Entry) => void;
  onSelectEntry: (entry: CalendarEntry) => void;
  onAdd: (iso: string) => void;
}
export function CalendarDay({
  iso,
  dayNumber,
  inMonth,
  isToday,
  isFocused = false,
  shoots,
  entries,
  events,
  selectedShootId,
  selectedEntryId,
  onSelectShoot,
  onSelectEntry,
  onAdd
}: CalendarDayProps) {
  const shown = events.slice(0, MAX_EVENTS_PER_DAY);
  const hidden = events.length - shown.length;

  /* One expression rather than two conditional classes: two `bg-*` utilities
     on the same element is a coin toss as to which the stylesheet applies. */
  const surface = isFocused ? 'bg-paper/[0.07] ring-1 ring-inset ring-paper/40' : isToday ? 'bg-paper/[0.05]' : '';
  return <div className={`group relative min-h-[6.5rem] border-b border-r border-ruleDark px-2 py-2 sm:min-h-[8rem] sm:px-3 ${inMonth ? '' : 'opacity-35'} ${surface}`} data-id="element-511" data-mp="3hi9lg6gg2510">
      <div className="flex items-start justify-between gap-2" data-id="element-512" data-mp="3qhwkwmpm50rf">
        <span className="flex items-center gap-2" data-id="element-513" data-mp="3qgbsp3qrttwq">
          {isToday ? <span aria-current="date" className="inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-[2px] bg-paper px-1.5 font-display text-lg leading-none text-ink" data-id="element-514" data-mp="3vgg80qfy0ild">
              {dayNumber}
            </span> : <span className="font-display text-lg leading-none text-paper/45" data-id="element-515" data-mp="37q3t2vma5lla">{dayNumber}</span>}
          {isToday && <span className="label-sm text-paper/70" data-id="element-516" data-mp="3ckgdb7vkz0y3">TODAY</span>}
        </span>

        {/* Kept out of the way until the cell is hovered or focused — 42
            permanent plus signs would read as clutter, not affordance.
            Focus-visible so it stays reachable by keyboard. */}
        <button type="button" onClick={() => onAdd(iso)} aria-label={`Add to ${iso}`} className="shrink-0 p-1 text-paper/0 transition-colors duration-150 ease-out group-hover:text-paper/40 hover:!text-paper focus-visible:text-paper/70 focus-visible:outline-none" data-id="element-517" data-mp="2chs37xuvm03b">
          <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-518" data-mp="1ykv5rdk95kdg" />
        </button>
      </div>

      {/* 1. Shoots — loudest. */}
      <ul className="m-0 mt-2 list-none space-y-1 p-0" data-id="element-519" data-mp="1ot8ujh7wzlap">
        {shoots.map(entry => {
        const selected = entry.shoot.id === selectedShootId;
        return <li key={`${entry.shoot.id}-${iso}`} data-id="element-520" data-mp="1jrjh5ux7yvjg">
              <button type="button" onClick={() => onSelectShoot(entry)} title={chipLabel(entry)} className={`block w-full truncate border-l-2 px-1.5 py-1 text-left font-sans text-[0.68rem] leading-tight tracking-wide2 transition-colors duration-150 ease-out ${selected ? 'border-l-paper bg-paper/15 text-paper' : 'border-l-paper/40 text-paper/70 hover:bg-paper/10 hover:text-paper'}`} data-id="element-521" data-mp="1evudbfe62shc">
                {chipLabel(entry)}
              </button>
            </li>;
      })}
      </ul>

      {/* 2. Portal OS's own diary entries — ours and editable, but not
          production, so they sit below the shoots and read quieter. A row
          still waiting to reach Google is marked, because "has Zeena got
          this yet" is the actual question behind the feature. */}
      {entries.length > 0 && <ul className="m-0 mt-1 list-none space-y-1 p-0" data-id="element-522" data-mp="1a60ea1zr0prv">
          {entries.map(entry => {
        const selected = entry.id === selectedEntryId;
        const waiting = entry.syncState !== 'synced';
        const label = `${entry.startTime ? `${entry.startTime} ` : ''}${entry.title}`;
        return <li key={`${entry.id}-${iso}`} data-id="element-523" data-mp="3nc6uj0eq69qv">
                <button type="button" onClick={() => onSelectEntry(entry)} title={[entry.title, entry.location].filter(Boolean).join(' — ')} className={`block w-full truncate border-l-2 px-1.5 py-1 text-left font-sans text-[0.66rem] leading-tight tracking-wide2 transition-colors duration-150 ease-out ${selected ? 'border-l-paper bg-paper/15 text-paper' : 'border-l-paper/20 text-paper/55 hover:bg-paper/10 hover:text-paper'}`} data-id="element-524" data-mp="3navmgoe8yfty">
                  {waiting && <span aria-hidden="true" className="mr-1 text-paper/30" data-id="element-525" data-mp="38f7mxcjvigwe">
                      ○
                    </span>}
                  {label}
                </button>
              </li>;
      })}
        </ul>}

      {/* 3. The company calendar — quieter and unbordered so the layers never
          get mistaken for each other. Read-only on purpose: it is somebody
          else's entry, so the link hands you to Google rather than pretending
          it can be edited here. Standing commitments are quieter still — they
          are things that are true every week, not things happening. */}
      {shown.length > 0 && <ul className="m-0 mt-1.5 list-none space-y-0.5 p-0" data-id="element-526" data-mp="1qi2xipicd9fh">
          {shown.map(event => <li key={`${event.eventId}-${iso}`} data-id="element-527" data-mp="1vh74ralpgx36">
              <a href={event.htmlLink || undefined} target="_blank" rel="noopener noreferrer" title={[event.summary, event.location].filter(Boolean).join(' — ')} className={`block truncate px-1.5 font-sans text-[0.64rem] leading-snug tracking-wide2 transition-colors duration-150 ease-out hover:text-paper/80 ${event.notProduction ? 'text-paper/20 italic' : 'text-paper/40'}`} data-id="element-528" data-mp="1vfvzf6cm3h9y">
                {event.startTime ? `${event.startTime} ` : ''}
                {event.summary}
              </a>
            </li>)}
          {hidden > 0 && <li className="px-1.5 font-sans text-[0.64rem] leading-snug tracking-wide2 text-paper/25" data-id="element-529" data-mp="3oz4r9ujhq30k">
              +{hidden} more
            </li>}
        </ul>}
    </div>;
}